import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ScopeBanner from "@/components/ScopeBanner";
import InstallWidget from "@/components/InstallWidget";
import NotificationsBell from "@/components/NotificationsBell";
import {
  Telescope, Table2, GitCompareArrows, Palette, Building2,
  ShieldAlert, Settings, LogOut, CreditCard, CalendarRange,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Research", icon: Table2, testid: "nav-research", roles: ["admin", "reviewer", "researcher"] },
  { to: "/compare", label: "Cross-House", icon: GitCompareArrows, testid: "nav-compare", roles: ["admin", "reviewer", "researcher"] },
  { to: "/artists", label: "Artists", icon: Palette, testid: "nav-artists", roles: ["admin", "reviewer", "researcher"] },
  { to: "/houses", label: "Auction Houses", icon: Building2, testid: "nav-houses", roles: ["admin", "reviewer", "researcher"] },
  { to: "/timeline", label: "Sales Timeline", icon: CalendarRange, testid: "nav-timeline", roles: ["admin", "reviewer", "researcher"] },
  { to: "/pricing", label: "Plans", icon: CreditCard, testid: "nav-pricing", roles: ["admin", "reviewer", "researcher"] },
  { to: "/quality", label: "Data Quality", icon: ShieldAlert, testid: "nav-quality", roles: ["admin", "reviewer"] },
  { to: "/admin", label: "Admin & Imports", icon: Settings, testid: "nav-admin", roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout, switchRole } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "researcher";
  const links = NAV.filter((n) => n.roles.includes(role));

  return (
    <div className="min-h-screen bg-[#0A0D14] text-slate-200 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-60 flex-col border-r border-slate-800/80 bg-[#0B0F18] sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800/80">
          <Telescope className="w-6 h-6 text-amber-400" />
          <span className="font-serif text-xl font-semibold tracking-tight">AuctionScope</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {links.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-amber-500/10 text-amber-400 border border-amber-500/30"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50 border border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800/80 text-[10px] font-mono uppercase tracking-widest text-slate-600">
          Pilot dataset · sample data
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-40 h-16 flex items-center justify-between px-4 lg:px-8 bg-[#0A0D14]/85 backdrop-blur-md border-b border-slate-800/80">
          <div className="md:hidden flex items-center gap-2">
            <Telescope className="w-5 h-5 text-amber-400" />
            <span className="font-serif text-lg font-semibold">AuctionScope</span>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <NotificationsBell />
            {user?.plan && (
              <span data-testid="plan-badge" className="al-chip border border-amber-500/30 text-amber-400 capitalize hidden sm:inline-flex">{user.plan}</span>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hidden sm:inline">Role</span>
              <select
                data-testid="role-switcher"
                value={role}
                onChange={(e) => switchRole(e.target.value)}
                className="bg-[#0A0D14] border border-slate-700 rounded-md px-2 py-1.5 text-xs text-amber-400 font-mono focus:outline-none focus:border-amber-500/60"
              >
                <option value="admin">Admin</option>
                <option value="reviewer">Reviewer</option>
                <option value="researcher">Researcher</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pl-3 border-l border-slate-800">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-slate-700" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-400 grid place-items-center text-xs font-semibold">
                  {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
                </div>
              )}
              <span className="text-sm text-slate-300 hidden lg:inline max-w-[140px] truncate">{user?.name || user?.email}</span>
              <button data-testid="logout-button" onClick={logout} className="text-slate-500 hover:text-red-400 transition-colors p-1.5">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </header>

        <ScopeBanner />

        {/* Mobile nav */}
        <div className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-slate-800/80 bg-[#0B0F18]">
          {links.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) =>
              `whitespace-nowrap px-3 py-1.5 rounded-md text-xs ${isActive ? "bg-amber-500/10 text-amber-400" : "text-slate-400"}`}>
              {n.label}
            </NavLink>
          ))}
        </div>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 max-w-[1700px] w-full mx-auto">{children}</main>
      </div>
      <InstallWidget />
    </div>
  );
}
